import React, { memo, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import { IconButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/migration/add';
import AddIconOld from '@atlaskit/icon/glyph/add';
import { colors } from '@atlaskit/theme';
import { B400, B50, N20A, N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { CHILD_CREATE_BTN_WRAPPER } from '../../constants.tsx';

export type ChildCreateContentProps = {
	tooltip: string;
	label: string;
	onClick: (event: SyntheticEvent<HTMLButtonElement>) => void;
	shouldDisplayBtn?: boolean;
};

export default memo<ChildCreateContentProps>((props: ChildCreateContentProps) => (
	<Tooltip content={props.tooltip}>
		<ChildCreateButtonWrapper
			id={CHILD_CREATE_BTN_WRAPPER}
			shouldDisplayBtn={props.shouldDisplayBtn}
			data-testid="business-list.common.ui.create-issue-plus-button.child-create-button-wrapper"
		>
			{isVisualRefreshEnabled() ? (
				<IconButton
					onClick={props.onClick}
					spacing="compact"
					appearance="subtle"
					label={props.label}
					icon={AddIcon}
				/>
			) : (
				<ChildCreateButton onClick={props.onClick}>
					<AddIconOld label={props.label} size="small" />
				</ChildCreateButton>
			)}
		</ChildCreateButtonWrapper>
	</Tooltip>
));

const BUTTON_SIZE = 2 * gridSize;
const ICON_SIZE = 12;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChildCreateButton = styled.button({
	padding: 0,
	margin: 0,
	border: 'none',
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${BUTTON_SIZE}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${BUTTON_SIZE}px`,
	borderRadius: token('border.radius', '3px'),
	verticalAlign: 'top',
	backgroundColor: token('color.background.neutral', N20A),
	color: token('color.text', N500),
	outline: 'none',
	transition: 'box-shadow 0.2s cubic-bezier(0.47, 0.03, 0.49, 1.38)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:focus.focus-visible': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		boxShadow: `inset 0 0 0 2px ${token('elevation.shadow.raised', colors.B100)}`,
	},

	'&:hover, &:focus': {
		backgroundColor: token('color.background.neutral.hovered', N20A),
	},
	'&:active': {
		backgroundColor: token('color.background.accent.gray.bolder', B50),
		color: token('color.background.accent.blue.subtle', B400),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: {
		width: `${ICON_SIZE}px`,
		height: `${ICON_SIZE}px`,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	svg: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		verticalAlign: 'top !important',
		stroke: token('color.text', N500),
		strokeWidth: '1px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChildCreateButtonWrapper = styled.div<{ shouldDisplayBtn?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: ({ shouldDisplayBtn }) => (shouldDisplayBtn ? 'flex' : 'none'),
});
